import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDialog, EmptyState, Field, PageHeader, Panel, RowsSkeleton } from "@/components/app/kit";
import { MedStatus, PatientPicker, PdfButton, StaffSelect, useStaff, nowLocal } from "@/components/med/shared";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/i18n";

const TEXTS = [["symptoms", "Symptômes"], ["observations", "Observations"], ["relevant_history", "Antécédents pertinents"], ["clinical_exam", "Examen clinique"],
  ["treatment_plan", "Plan de traitement / conduite à tenir"], ["exams_prescribed", "Examens prescrits"], ["report", "Compte rendu"]];
const VITALS = [["bp_systolic", "TA systolique (mmHg)"], ["bp_diastolic", "TA diastolique (mmHg)"], ["temperature", "Température (°C)"], ["weight", "Poids (kg)"],
  ["height", "Taille (cm)"], ["heart_rate", "Fréquence cardiaque (bpm)"], ["spo2", "SpO2 (%)"]];

export function ConsultationsList() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const staff = useStaff();
  const { data } = useFetch("/t/consultations");
  const [nw, setNw] = useState(null);
  const create = async () => {
    try {
      const { data: c } = await api.post("/t/consultations", { ...nw, date: nowLocal() });
      navigate(`/cabinet/consultations/${c.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Clinique" title="Consultations" actions={can("consultations:write") && <Button onClick={() => setNw({ reason: "" })} data-testid="consult-new"><Plus className="h-4 w-4 mr-2" />Nouvelle consultation</Button>} />
      <Panel className="overflow-hidden"><div className="-m-5 overflow-x-auto">
        {!data ? <RowsSkeleton /> : !data.length ? <EmptyState title="Aucune consultation" /> : (
          <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Patient</TableHead><TableHead>Praticien</TableHead><TableHead>Motif</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>{data.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/cabinet/consultations/${c.id}`)} data-testid={`consult-row-${c.id}`}>
                <TableCell className="text-xs">{c.date.replace("T", " ")}</TableCell><TableCell className="font-medium">{c.patient_name}</TableCell><TableCell>{c.staff_name}</TableCell><TableCell>{c.reason}</TableCell><TableCell><MedStatus s={c.status} /></TableCell>
              </TableRow>))}</TableBody></Table>)}
      </div></Panel>
      {nw && <ConfirmDialog open onOpenChange={(o) => !o && setNw(null)} title="Nouvelle consultation" confirmLabel="Créer le brouillon" onConfirm={create}>
        <div className="space-y-3"><PatientPicker value={nw.patient_id} onChange={(v) => setNw({ ...nw, patient_id: v })} testid="consult-patient" />
          <StaffSelect value={nw.practitioner_id} onChange={(v) => setNw({ ...nw, practitioner_id: v })} staff={staff} onlyDoctors testid="consult-practitioner" />
          <Input placeholder="Motif" value={nw.reason} onChange={(e) => setNw({ ...nw, reason: e.target.value })} data-testid="consult-reason" /></div>
      </ConfirmDialog>}
    </div>
  );
}

export default function ConsultationEditor() {
  const { id } = useParams();
  const { can } = useAuth();
  const staff = useStaff();
  const { data, error, reload, setData } = useFetch(`/t/consultations/${id}`);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [fin, setFin] = useState(false);
  if (error) return <EmptyState icon={Lock} title="Accès impossible" text={error} />;
  if (!data) return <RowsSkeleton rows={8} />;
  const final = data.status === "finalized";
  const editable = can("consultations:write") && (!final || can("consultations:finalize"));
  const set = (k, v) => setData({ ...data, [k]: v });
  const setV = (k, v) => setData({ ...data, vitals: { ...data.vitals, [k]: v === "" ? null : Number(v) } });
  const save = async () => {
    setBusy(true);
    try {
      const keys = ["patient_id", "practitioner_id", "appointment_id", "date", "reason", "diagnosis", "diagnosis_code", "next_appointment", "vitals", ...TEXTS.map((t) => t[0])];
      const body = Object.fromEntries(keys.map((k) => [k, data[k] ?? (k === "appointment_id" ? null : "")]));
      setData((await api.put(`/t/consultations/${id}`, { ...body, version: data.version, amendment_reason: reason })).data);
      setReason("");
      toast.success(final ? "Correction tracée enregistrée" : "Brouillon enregistré");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const finalize = async () => {
    try {
      await save();
      await api.post(`/t/consultations/${id}/finalize`);
      toast.success("Consultation finalisée");
      setFin(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const ta = (k, l) => <Field key={k} label={l}><Textarea rows={3} value={data[k] || ""} disabled={!editable} onChange={(e) => set(k, e.target.value)} data-testid={`consult-${k}`} /></Field>;
  return (
    <div>
      <PageHeader eyebrow="Consultation" title={<span className="flex items-center gap-3">{data.reason}<MedStatus s={data.status} /></span>}
        subtitle={<Link to={`/cabinet/patients/${data.patient_id}`} className="text-sky-700 hover:underline">Voir le dossier patient</Link>}
        actions={<>
          <PdfButton kind="consultation" id={id} perm="consultations:print" />
          {editable && <Button variant="outline" onClick={save} disabled={busy} data-testid="consult-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{final ? "Enregistrer la correction" : "Enregistrer le brouillon"}</Button>}
          {!final && can("consultations:finalize") && <Button onClick={() => setFin(true)} data-testid="consult-finalize">Finaliser</Button>}
        </>} />
      {final && editable && <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4"><Field label="Motif de correction (obligatoire pour modifier une note finalisée)"><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="consult-amend-reason" /></Field></div>}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-5">
          <Panel title="Motif et diagnostic"><div className="grid sm:grid-cols-2 gap-4">
            <Field label="Date"><Input type="datetime-local" value={data.date} disabled={!editable} onChange={(e) => set("date", e.target.value)} /></Field>
            <Field label="Praticien"><StaffSelect value={data.practitioner_id} onChange={(v) => editable && set("practitioner_id", v)} staff={staff} onlyDoctors /></Field>
            <Field label="Motif" className="sm:col-span-2"><Input value={data.reason} disabled={!editable} onChange={(e) => set("reason", e.target.value)} /></Field>
            <Field label="Diagnostic"><Input value={data.diagnosis || ""} disabled={!editable} onChange={(e) => set("diagnosis", e.target.value)} data-testid="consult-diagnosis" /></Field>
            <Field label="Code (CIM-10 ou nomenclature du cabinet)"><Input value={data.diagnosis_code || ""} disabled={!editable} onChange={(e) => set("diagnosis_code", e.target.value)} className="font-mono" /></Field>
            <Field label="Prochain rendez-vous" className="sm:col-span-2"><Input value={data.next_appointment || ""} disabled={!editable} onChange={(e) => set("next_appointment", e.target.value)} /></Field>
          </div></Panel>
          <Panel title="Notes cliniques"><div className="space-y-4">{TEXTS.map(([k, l]) => ta(k, l))}</div></Panel>
        </div>
        <div className="lg:col-span-4 space-y-5">
          <Panel title="Constantes"><div className="grid grid-cols-2 gap-3">{VITALS.map(([k, l]) => (
            <Field key={k} label={l}><Input type="number" step="0.1" value={data.vitals?.[k] ?? ""} disabled={!editable} onChange={(e) => setV(k, e.target.value)} data-testid={`vital-${k}`} /></Field>))}</div></Panel>
          <Panel title="Traçabilité">
            <p className="text-xs text-slate-500">Version {data.version}{final && ` · finalisée le ${fmtDateTime(data.finalized_at)} par ${data.finalized_by}`}</p>
            {(data.amendments || []).map((a, i) => <p key={i} className="text-xs mt-2 border-t border-slate-100 pt-2" data-testid="consult-amendment">{fmtDateTime(a.at)} — {a.by} : « {a.reason} » ({Object.keys(a.changes).join(", ")})</p>)}
          </Panel>
        </div>
      </div>
      <ConfirmDialog open={fin} onOpenChange={setFin} onConfirm={finalize} confirmLabel="Finaliser" title="Finaliser la consultation ?" description="Une consultation finalisée ne peut plus être supprimée ; toute modification ultérieure sera tracée avec un motif." />
    </div>
  );
}
